/**
 * Bounded, PII-free helpers that let failed-call telemetry be diagnosed.
 *
 * Everything here emits a closed vocabulary (engine enum, small int, URL
 * *schemes*, a 22-char opaque room id) — never a raw UA string, URL, host or
 * credential. The analytics handler deliberately stores user_agent as NULL;
 * that promise stands, so the browser identity is derived client-side and
 * reduced to engine + major before it leaves the page.
 */

export type BrowserEngine = 'blink' | 'webkit' | 'gecko' | 'other';
export type BrowserIdentity = { engine: BrowserEngine; major: number };

type UaBrand = { brand: string; version: string };

const major = (raw: string | undefined): number => Math.min(parseInt(raw ?? '', 10) || 0, 999);

/** [engine, regex with the major as group 1], first match wins. iOS first: every iOS browser is WebKit. */
const UA_RULES: ReadonlyArray<readonly [BrowserEngine, RegExp]> = [
	['webkit', /\b(?:iPhone|iPad|iPod)\b[^]*?\bOS (\d+)[_.]/],
	['gecko', /\bFirefox\/(\d+)/],
	['blink', /\b(?:Chrome|Chromium|Edg|OPR)\/(\d+)/],
	['webkit', /\bVersion\/(\d+)[^]*\bSafari\//],
];

/** Pure: engine + major from UA-CH brands when present (Blink only), else a coarse UA parse. */
export function parseBrowserIdentity(ua: string, brands?: readonly UaBrand[] | null): BrowserIdentity {
	const chromium = Array.isArray(brands) ? brands.find((b) => b?.brand === 'Chromium') : undefined;
	if (chromium) return { engine: 'blink', major: major(chromium.version) };
	for (const [engine, re] of UA_RULES) {
		const m = re.exec(typeof ua === 'string' ? ua : '');
		if (m) return { engine, major: major(m[1]) };
	}
	return { engine: 'other', major: 0 };
}

let cachedBrowser: BrowserIdentity | null = null;

/** Engine + major of the running browser. Cached; never throws. */
export function getBrowserIdentity(): BrowserIdentity {
	if (cachedBrowser) return cachedBrowser;
	let id: BrowserIdentity = { engine: 'other', major: 0 };
	try {
		if (typeof navigator !== 'undefined') {
			const uaData = (navigator as { userAgentData?: { brands?: readonly UaBrand[] } }).userAgentData;
			id = parseBrowserIdentity(navigator.userAgent ?? '', uaData?.brands);
		}
	} catch { /* identity is best-effort */ }
	cachedBrowser = id;
	return id;
}

const ROOM_ID_22 = /^[A-Za-z0-9_-]{22}$/;
/** Route prefixes that put an opaque room id in the second path segment. */
const ROOM_ROUTE_PREFIXES = new Set(['r', 'c', 'm', 'g']);

/**
 * The opaque 22-char room id of a call route, or undefined. Accepts `/<id>`,
 * `/r/<id>`, `/c/<id>`, `/m/<id>`, `/g/<id>` (a trailing sub-path is ignored). Nothing
 * else from the URL — no query, no hash, no other path — ever leaves here.
 * Mirrored by ROOM_ID_RE in static/sw.js (a test keeps them in sync).
 */
export function roomIdFromPathname(pathname: string | null | undefined): string | undefined {
	if (typeof pathname !== 'string') return undefined;
	const seg = pathname.split('/').filter(Boolean);
	if (seg.length === 0) return undefined;
	const candidate = ROOM_ROUTE_PREFIXES.has(seg[0]) ? seg[1] : seg[0];
	return candidate !== undefined && ROOM_ID_22.test(candidate) ? candidate : undefined;
}

/** Room id of the page the user is on right now, if it is a call route. */
export function currentRoomId(): string | undefined {
	try {
		return typeof location === 'undefined' ? undefined : roomIdFromPathname(location.pathname);
	} catch {
		return undefined;
	}
}
